import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X, MapPin, Phone } from "lucide-react";
import { apiClient } from "../../lib/apiClient";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import { formatDate } from "../../lib/utils";

export function CustomerRouteSearch({ filters, testId = "route-customer-search" }) {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const debouncedQ = useDebouncedValue(q, 350);
  const [open, setOpen] = useState(false);
  const [state, setState] = useState({ loading: false, error: null, results: [] });
  const boxRef = useRef(null);

  useEffect(() => {
    function onClickOutside(e) {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  useEffect(() => {
    if (!debouncedQ || debouncedQ.trim().length < 2) {
      setState({ loading: false, error: null, results: [] });
      return;
    }
    let cancelled = false;
    setState((s) => ({ ...s, loading: true, error: null }));
    apiClient
      .get("/routes/search-customers", {
        params: {
          q: debouncedQ.trim(),
          date_from: filters?.from || undefined,
          date_to: filters?.to || undefined,
          driver_id: filters?.driverId && filters.driverId !== "all" ? filters.driverId : undefined,
          packing_state: filters?.packingState && filters.packingState !== "all" ? filters.packingState : undefined,
        },
      })
      .then(({ data }) => { if (!cancelled) setState({ loading: false, error: null, results: data }); })
      .catch(() => { if (!cancelled) setState({ loading: false, error: "Search failed. Try again.", results: [] }); });
    return () => { cancelled = true; };
  }, [debouncedQ, filters?.from, filters?.to, filters?.driverId, filters?.packingState]);

  const goTo = (r) => {
    setOpen(false);
    setQ("");
    navigate(`/routes/${r.route_id}?highlight=${r.delivery_id}`);
  };

  let prevDate;

  return (
    <div className="relative w-full max-w-sm" ref={boxRef} data-testid={testId}>
      <div className="relative">
        <Search size={15} strokeWidth={1.5} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
        <input
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder="Find a customer — name, phone, or address..."
          className="w-full rounded-md border border-border bg-surface py-2 pl-9 pr-8 text-sm placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary/30"
          data-testid={`${testId}-input`}
        />
        {q && (
          <button type="button" onClick={() => { setQ(""); setState({ loading: false, error: null, results: [] }); }} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-main" data-testid={`${testId}-clear-button`}>
            <X size={14} strokeWidth={1.5} />
          </button>
        )}
      </div>

      {open && q.trim().length >= 2 && (
        <div className="absolute z-40 mt-1 max-h-96 w-full overflow-y-auto rounded-md border border-border bg-surface shadow-lg" data-testid={`${testId}-dropdown`}>
          {state.loading ? (
            <p className="p-3 text-xs text-text-muted">Searching...</p>
          ) : state.error ? (
            <p className="p-3 text-xs text-danger">{state.error}</p>
          ) : state.results.length === 0 ? (
            <p className="p-3 text-xs text-text-muted" data-testid={`${testId}-empty`}>
              No matching customer found on a route in the current filters.
            </p>
          ) : (
            <>
            {state.results.map((r) => {
              const showDateHeader = r.route_date !== prevDate;
              prevDate = r.route_date;
              return (
                <React.Fragment key={r.delivery_id}>
                  {showDateHeader && (
                    <div className="border-t border-border bg-background px-3 py-1 text-xs font-semibold uppercase tracking-wide text-text-muted first:border-t-0">
                      {formatDate(r.route_date)}
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => goTo(r)}
                    className="flex w-full items-start gap-3 border-b border-border px-3 py-2.5 text-left last:border-b-0 hover:bg-background"
                    data-testid={`${testId}-result-${r.delivery_id}`}
                  >
                    <span className="mt-0.5 flex h-6 min-w-6 items-center justify-center rounded-full bg-primary px-1.5 text-xs font-semibold text-white">
                      {r.delivery_order_id ?? "—"}
                    </span>
                    <span className="flex-1">
                      <span className="block text-sm font-medium text-text-main">{r.customer_name || `#${r.customer_id}`}</span>
                      <span className="mt-0.5 flex flex-wrap items-center gap-x-3 text-xs text-text-muted">
                        {r.phone && <span className="flex items-center gap-1"><Phone size={11} strokeWidth={1.5} /> {r.phone}</span>}
                        {r.address && <span className="flex items-center gap-1 truncate"><MapPin size={11} strokeWidth={1.5} /> {r.address}</span>}
                      </span>
                      <span className="mt-0.5 block text-xs text-text-muted">
                        Route #{r.route_id} · {r.driver_name || "Unassigned"} · Stop {r.delivery_order_id ?? "—"}
                      </span>
                    </span>
                  </button>
                </React.Fragment>
              );
            })}
            {state.results.length >= 100 && (
              <p className="border-t border-border bg-background px-3 py-1.5 text-center text-xs text-text-muted" data-testid={`${testId}-limit-hint`}>
                Showing first 100 matches — narrow your search or filters for more precise results
              </p>
            )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
